import { makeAutoObservable, observable } from 'mobx'

import { OnBoardingType } from '../api/user/User.types'
import { RootStore } from './RootStore'
import { cloneDeep } from 'lodash'

export enum MarkAllAsReadStatus {
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  ERROR = 'ERROR'
}

export class UserStore {
  id: string
  email: string
  phoneNumber: string
  customerStripeId: any
  isSmsOptedIn: boolean
  userRole: string
  firstName: string
  lastName: string
  organizationPreference: any
  organizations: any[]
  organizationMemberTotalCount: any
  planType: any
  organizationDetails: any
  featureUsageData: any
  avatar: string
  isVerified: boolean
  selectedOrganization: any
  notificationCount: number
  invitationCount: number
  noActiveSubscription: boolean
  loggedIn = false
  onBoardedVia: OnBoardingType
  hasMarkAllNotificationsReadCompleted: boolean
  markAllNotificationsReadStatus: MarkAllAsReadStatus

  constructor(private rootStore: RootStore) {
    this.id = ''
    this.email = ''
    this.phoneNumber = ''
    this.customerStripeId = ''
    this.organizationMemberTotalCount
    this.planType
    this.isSmsOptedIn = false
    this.userRole = ''
    this.firstName = ''
    this.lastName = ''
    this.avatar = ''
    this.organizationPreference = {}
    this.organizations = []
    this.organizationDetails
    this.featureUsageData
    this.isVerified = false
    this.selectedOrganization = {}
    this.isVerified = false
    this.loggedIn = false
    this.notificationCount = 0
    this.invitationCount = 0
    this.noActiveSubscription = false
    this.onBoardedVia = OnBoardingType.EMAIL
    this.hasMarkAllNotificationsReadCompleted = false
    this.markAllNotificationsReadStatus = MarkAllAsReadStatus.COMPLETED
    makeAutoObservable(this, {
      id: observable.struct,
      email: observable.struct,
      phoneNumber: observable.struct,
      customerStripeId: observable.struct,
      isSmsOptedIn: observable.struct,
      userRole: observable.struct,
      selectedOrganization: observable.struct,
      organizations: observable.struct,
      organizationDetails: observable.struct,
      featureUsageData: observable.struct,
      organizationMemberTotalCount: observable.struct,
      planType: observable.struct,
      isVerified: observable.struct,
      notificationCount: observable.struct,
      invitationCount: observable.struct,
      hasMarkAllNotificationsReadCompleted: observable.struct,
      markAllNotificationsReadStatus: observable.struct,
      noActiveSubscription: observable.struct
    })
  }
  setId(id: string) {
    this.id = id
  }

  getId() {
    return this.id
  }

  setOrganizationPreference(organizationPreference: any) {
    this.organizationPreference = organizationPreference
  }

  getOrganizationPreference() {
    return this.organizationPreference
  }

  setIsVerified(isVerified: boolean) {
    this.isVerified = isVerified
  }

  getIsVerified() {
    return this.isVerified
  }

  setVerification(isVerified: boolean) {
    this.isVerified = isVerified
  }

  getVerification() {
    return this.isVerified
  }

  setAvatar(avatar: string) {
    this.avatar = avatar
  }

  getAvatar() {
    return this.avatar
  }

  setEmail(email: string) {
    this.email = email
  }

  getEmail() {
    return this.email
  }

  setIsSmsOptedIn(isPhoneChecked: boolean) {
    this.isSmsOptedIn = isPhoneChecked
  }

  getIsSmsOptedIn() {
    return this.isSmsOptedIn
  }

  setPhoneNumber(phoneNumber: string) {
    this.phoneNumber = phoneNumber
  }

  getPhoneNumber() {
    return this.phoneNumber
  }

  setCustomerStripeId(customerStripeId: string) {
    this.customerStripeId = customerStripeId
  }

  getCustomerStripeId() {
    return this.customerStripeId
  }

  setUserRole(userRole: string) {
    this.userRole = userRole
  }

  getUserRole() {
    return this.userRole
  }

  setFirstName(firstName: string) {
    this.firstName = firstName
  }

  setLastName(lastName: string) {
    this.lastName = lastName
  }

  getFirstName() {
    return this.firstName
  }
  getLastName() {
    return this.lastName
  }

  setPlanType(planType: any) {
    this.planType = planType
  }

  setOrganizations(organizations: any[]) {
    this.organizations = organizations
  }

  setOrganizationMemberTotalCount(organizationMemberTotalCount: any) {
    this.organizationMemberTotalCount = organizationMemberTotalCount
  }
  setSelectedOrganization(selectedOrganization: any) {
    this.rootStore.userStore.selectedOrganization = { ...selectedOrganization }
  }

  setOrganizationDetails(organizationDetails: any) {
    this.organizationDetails = organizationDetails
  }

  setFeatureUsageData(featureUsageData: any) {
    this.featureUsageData = featureUsageData
  }

  getSelectedOrganization() {
    return this.rootStore.userStore.selectedOrganization
  }

  getOrganizations() {
    return this.organizations
  }

  getOrganizationMemberTotalCount() {
    return this.organizationMemberTotalCount
  }

  getPlanType() {
    return this.planType
  }

  getOrganizationDetails() {
    return this.organizationDetails
  }

  getFeatureUsageData() {
    return this.featureUsageData
  }
  getNotificationCount() {
    return this.notificationCount
  }
  setNotificationCount(notificationCount: number) {
    this.notificationCount = notificationCount
  }
  getInvitationCount() {
    return this.invitationCount
  }
  setInvitationCount(invitationCount: number) {
    this.invitationCount = invitationCount
  }

  getHasMarkAllNotificationReadCompleted() {
    return this.hasMarkAllNotificationsReadCompleted
  }

  setHasMarkAllNotificationReadCompleted(markAllAsReadFlag: boolean) {
    this.hasMarkAllNotificationsReadCompleted = markAllAsReadFlag
  }

  getMarkAllNotificationsReadStatus() {
    return this.markAllNotificationsReadStatus
  }

  setMarkAllNotificationsReadStatus(markAllAsReadStatus: MarkAllAsReadStatus) {
    this.markAllNotificationsReadStatus = markAllAsReadStatus
  }

  getIsLoggedIn() {
    return this.loggedIn
  }

  setIsLoggedIn(isLoggedIn: boolean) {
    this.loggedIn = isLoggedIn
  }

  setAvatarById(id: string, avatar: string) {
    const newOrgs = cloneDeep(this.organizations)
    const orgIndex = this.organizations.findIndex((org) => org.id === id)
    newOrgs[orgIndex].avatar = avatar
    this.setOrganizations(newOrgs)
  }

  getNoActiveSubscription() {
    return this.noActiveSubscription
  }

  setNoActiveSubscription(noActiveSubscription: boolean) {
    this.noActiveSubscription = noActiveSubscription
  }

  setOnBoardedVia(onBoardedVia: OnBoardingType) {
    this.onBoardedVia = onBoardedVia
  }

  getOnBoardedVia() {
    return this.onBoardedVia
  }

  reset() {
    this.id = ''
    this.email = ''
    this.phoneNumber = ''
    this.customerStripeId = ''
    this.isSmsOptedIn = false
    this.userRole = ''
    this.firstName = ''
    this.lastName = ''
    this.avatar = ''
    this.organizationPreference = {}
    this.organizations = []
    this.organizationDetails
    this.featureUsageData
    this.organizationMemberTotalCount
    this.planType
    this.isVerified = false
    this.selectedOrganization = {}
    this.isVerified = false
    this.hasMarkAllNotificationsReadCompleted = false
    this.markAllNotificationsReadStatus = MarkAllAsReadStatus.COMPLETED
    this.notificationCount = 0
    this.invitationCount = 0
    this.noActiveSubscription = false
    this.onBoardedVia = OnBoardingType.EMAIL
  }
}
